import mustache from "mustache";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { CultivoResponse } from "../../../../../modulos/catalogos/aplicaciones/response/CultivoResponse";
import { cultivoGrpcCatalogService } from "../../../../../modulos/catalogos/aplicaciones/services/CultivoGrpcCatalogService";
import { CultivoItem } from "../../../../../generated/grpc/catalogos_aplicaciones_pb";
import { MonitoreoPlagaEnfermedadItem } from "../../../../../generated/grpc/catalogos_monitoreo_pb";
import { monitoreoPlagaEnfermedadGrpcCatalogService } from "../../../../../modulos/catalogos/monitoreo/services/MonitoreoPlagaEnfermedadGrpcCatalogService";
import { fx_createMonitoreoRelacionFincaCultivoPlagaGrpcService } from "../../../../../modulos/configuracion/monitoreo/services/MonitoreoRelacionFincaCultivoPlagaGrpcService";
import { ClienteFincaLoteResponse } from "../../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { fx_createLoteGrpcService } from "../../../../../modulos/terceros/services/LoteGrpcService";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import { ArbolesMonitorearRender } from "../form/ArbolesMonitorearRender";
import { CultivoPlagasRender } from "../form/CultivoPlagasRender";
import { MonitoresRender } from "../list/MonitoresRender";
import template from "./monitoreoConfiguracionContentTemplate.html";

mustache.parse(template);

/** Contador incremental de instancias, para no colisionar ids en el DOM. */
let CONTENT_ID: number = 0;

/** Banderas de monitoreo de plagas compartidas por `CultivoResponse` y
 *  `MonitoreoPlagaEnfermedadItem`. El cruce de ambas decide qué plagas se
 *  ofrecen para cada cultivo: se usan estas y no `tipoMonitoreoId` porque una
 *  misma plaga puede aplicar a varios cultivos a la vez. */
const BANDERAS_MONITOREO = [
  "isMonitoreoCitricos",
  "isMonitoreoAguacateHass",
  "isMonitoreoMango",
  "isMonitoreoMacadamia",
  "isMonitoreoGuanabana",
  "isMonitoreoPapa",
] as const;

/** Si el cultivo participa del monitoreo de plagas (café queda afuera: ese es
 *  el módulo de broca, que tiene su propia pantalla). */
const fx_tieneMonitoreoPlagas = (cultivo: CultivoResponse): boolean =>
  BANDERAS_MONITOREO.some((bandera) => cultivo[bandera] === true);

/** Si una plaga aplica a un cultivo: comparten al menos una bandera activa. */
const fx_aplicaACultivo = (
  plaga: MonitoreoPlagaEnfermedadItem,
  cultivo: CultivoResponse,
): boolean =>
  BANDERAS_MONITOREO.some(
    (bandera) => cultivo[bandera] === true && plaga[bandera] === true,
  );

/** El cultivo del lote con las banderas de monitoreo del catálogo de cultivos. Desde el 2026-09-23 el
 *  cultivo del lote solo trae `cantidadPlagas`, no las banderas, y sin ellas esta ventana no mostraba
 *  ningún cultivo (arreglado el 2026-10-07). El catálogo de cultivos sí las trae y ya está guardado en
 *  el navegador. Si el cultivo no está en el catálogo, queda como viene (sin banderas) y no se muestra. */
const fx_conBanderasDelCatalogo = (
  cultivo: CultivoResponse | null,
  catalogo: Map<string, CultivoItem>,
): CultivoResponse | null => {
  if (!cultivo) return null;
  const item = catalogo.get(cultivo.id);
  if (!item) {
    console.warn(
      `Configuración de monitoreo: el cultivo ${cultivo.id} (${cultivo.nombre}) no está en el catálogo local de cultivos.`,
    );
    return cultivo;
  }
  return {
    ...cultivo,
    isMonitoreoCitricos: item.isMonitoreoCitricos,
    isMonitoreoAguacateHass: item.isMonitoreoAguacateHass,
    isMonitoreoMango: item.isMonitoreoMango,
    isMonitoreoMacadamia: item.isMonitoreoMacadamia,
    isMonitoreoGuanabana: item.isMonitoreoGuanabana,
    isMonitoreoPapa: item.isMonitoreoPapa,
  };
};

/** Cultivo de la finca con los lotes que lo tienen sembrado. */
type CultivoConLotes = {
  cultivo: CultivoResponse;
  lotes: string[];
};

/** Cuerpo del modal de configuración de monitoreo: apila las secciones en un
 *  contenedor scrolleable, cada una como una card (mismo patrón que las
 *  secciones de los formularios).
 *
 *  Los árboles se pintan de entrada (el dato ya viaja en la finca activa); las
 *  cards de cultivo necesitan lotes + catálogo + relaciones, así que se cargan
 *  después y se inyectan en su contenedor. */
export class MonitoreoConfiguracionContentRender implements IConnectRender {
  /** Id de instancia, para los ids del DOM. */
  private readonly _id: number = ++CONTENT_ID;

  /** Sección de árboles a monitorear (guardado automático). */
  private readonly _arboles: ArbolesMonitorearRender =
    new ArbolesMonitorearRender();

  /** Sección de monitores de campo (alcance cliente, no finca). */
  private readonly _monitores: MonitoresRender = new MonitoresRender();

  /** Una card por cultivo de la finca con monitoreo de plagas. */
  private _cultivos: CultivoPlagasRender[] = [];

  render(): string {
    return mustache.render(template, {
      id: this._id,
      arboles: this._arboles.render(),
      monitores: this._monitores.render(),
    });
  }

  connect(): void {
    this._arboles.connect();
    this._monitores.connect();
    this._wrapper = document.getElementById(`mcCultivos-${this._id}`);
    this._cargarCultivos();
  }

  dispose(): void {
    this._disposed = true;
    this._arboles.dispose();
    this._monitores.dispose();
    this._cultivos.forEach((cultivo) => cultivo.dispose());
    this._cultivos = [];
  }

  /** Contenedor donde se inyectan las cards de cultivo. */
  private _wrapper: HTMLElement | null = null;

  /** Marca de componente desmontado: nada debe tocar el DOM después. */
  private _disposed: boolean = false;

  /** Carga lotes, catálogo de plagas y relaciones ya guardadas, arma una card
   *  por cultivo y las monta. */
  private _cargarCultivos(): void {
    const finca = serviceProvider.getFincaSelectedService().getRequired();

    Promise.all([
      fx_createLoteGrpcService(finca.id).getListAsync(),
      monitoreoPlagaEnfermedadGrpcCatalogService.getListAsync(),
      fx_createMonitoreoRelacionFincaCultivoPlagaGrpcService(
        finca.id,
      ).getListAsync(),
      cultivoGrpcCatalogService.getListAsync(),
    ])
      .then(([lotes, plagas, relaciones, catalogoCultivos]) => {
        if (this._disposed || !this._wrapper) return;

        const cultivos = this._agruparPorCultivo(lotes, catalogoCultivos);
        if (cultivos.length === 0) {
          this._wrapper.innerHTML = `<div class="sx-empty">Esta finca no tiene cultivos con monitoreo de plagas.</div>`;
          return;
        }

        this._cultivos = cultivos.map(
          (item, index) =>
            new CultivoPlagasRender(
              finca.id,
              item.cultivo,
              item.lotes,
              plagas.filter((plaga) => fx_aplicaACultivo(plaga, item.cultivo)),
              relaciones.filter(
                (relacion) => relacion.cultivoId === item.cultivo.id,
              ),
              index === 0,
            ),
        );

        this._wrapper.innerHTML = this._cultivos
          .map((cultivo) => cultivo.render())
          .join("");
        this._cultivos.forEach((cultivo) => cultivo.connect());
      })
      .catch((error) => {
        if (this._disposed) return;
        serviceProvider
          .getErrorService()
          .addError("Error al cargar las plagas por cultivo", error);
      });
  }

  /** Agrupa los lotes de la finca por cultivo, quedándose solo con los que
   *  participan del monitoreo de plagas. El orden es el de aparición de los
   *  lotes, que ya vienen ordenados del store. */
  private _agruparPorCultivo(
    lotes: ClienteFincaLoteResponse[],
    catalogoCultivos: CultivoItem[],
  ): CultivoConLotes[] {
    const catalogoPorId = new Map(catalogoCultivos.map((item) => [item.id, item]));
    const porCultivo = new Map<string, CultivoConLotes>();

    for (const lote of lotes) {
      const cultivo = fx_conBanderasDelCatalogo(lote.cultivo, catalogoPorId);
      if (!cultivo || !fx_tieneMonitoreoPlagas(cultivo)) continue;

      const existente = porCultivo.get(cultivo.id);
      if (existente) {
        existente.lotes.push(lote.nombreLote);
        continue;
      }

      porCultivo.set(cultivo.id, {
        cultivo: cultivo,
        lotes: [lote.nombreLote],
      });
    }

    return Array.from(porCultivo.values());
  }
}
